import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import { Link } from 'react-router-dom';
import { useConsent } from '../context/ConsentContext';
import { useTranslation } from 'react-i18next';
import { loadLanguage } from '../i18n';
import AuthMenu from '../components/auth/AuthMenu';
import SignInModal from '../components/auth/SignInModal';
import { useAuth } from '../context/AuthContext';
import { Instagram, Menu, X } from 'lucide-react';
import logoImage80 from '../assets/logo.jpg?w=80&format=jpg&imagetools';
import logoImage160 from '../assets/logo.jpg?w=160&format=jpg&imagetools';
import logoImage240 from '../assets/logo.jpg?w=240&format=jpg&imagetools';
import spinBg from '../assets/spin-background.webp';
import { Helmet } from 'react-helmet-async';
import { useSpinWheelEnabled } from '../hooks/useSpinWheelEnabled';


type Prize = {
  id: string;
  title: string;
  description: string;
};

export default function SpinWheel() {
  const { t, i18n } = useTranslation();
  const { openManager } = useConsent();
  const { state } = useAuth();
  const spinWheelEnabled = useSpinWheelEnabled();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [wheelRotation, setWheelRotation] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showCouponModal, setShowCouponModal] = useState(false);
  const [couponId, setCouponId] = useState<string | null>(null);
  const barcodeRef = useRef<SVGSVGElement | null>(null);
  const [nextAllowedAt, setNextAllowedAt] = useState<number | null>(null);
  const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
  const [countdownText, setCountdownText] = useState<string | null>(null);
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [showPrizeSelection, setShowPrizeSelection] = useState(true);

  useEffect(() => {
    try {
      const ga = (window as any).gtag as undefined | ((...args: any[]) => void);
      if (ga) {
        ga('event', 'page_view', { page_path: '/spin', page_title: 'Spin Wheel' });
      }
    } catch {}
  }, []);

  useEffect(() => {
    async function loadCooldown() {
      if (!state.user) return;
      try {
        const { data } = await supabase.auth.getUser();
        const last = (data.user as any)?.user_metadata?.wheelLastSpinAt as number | undefined;
        let lastSpinAt = typeof last === 'number' ? last : undefined;
        if (!lastSpinAt) {
          const ls = localStorage.getItem(`wheelLastSpinAt:${state.user.id}`);
          if (ls) lastSpinAt = Number(ls);
        }
        if (lastSpinAt) setNextAllowedAt(lastSpinAt + COOLDOWN_MS);
      } catch {}
    }
    loadCooldown();
  }, [state.user]);

  useEffect(() => {
    let timer: number | undefined;
    function update() {
      if (!nextAllowedAt) return;
      const now = Date.now();
      if (now >= nextAllowedAt) {
        setCountdownText(null);
        if (timer) window.clearInterval(timer);
        return;
      }
      setCountdownText(formatCountdownPrecise(nextAllowedAt - now));
    }
    if (nextAllowedAt && Date.now() < nextAllowedAt) {
      update();
      timer = window.setInterval(update, 1000);
    }
    return () => { if (timer) window.clearInterval(timer); };
  }, [nextAllowedAt]);

  const prizes: Prize[] = [
    { id: 'combo2', title: t('wheelV2.prizes.combo2.title'), description: t('wheelV2.prizes.combo2.description') },
    { id: 'combo3', title: t('wheelV2.prizes.combo3.title'), description: t('wheelV2.prizes.combo3.description') },
    { id: 'combo4', title: t('wheelV2.prizes.combo4.title'), description: t('wheelV2.prizes.combo4.description') },
    { id: 'discount5', title: t('wheelV2.prizes.discount5.title'), description: t('wheelV2.prizes.discount5.description') },
    { id: 'discount10', title: t('wheelV2.prizes.discount10.title'), description: t('wheelV2.prizes.discount10.description') },
    { id: 'discount20', title: t('wheelV2.prizes.discount20.title'), description: t('wheelV2.prizes.discount20.description') },
  ];

  const segments = [
    { id: 'win', weight: 50, color: '#6A1E55', labelColor: '#F2F9FF' },
    { id: 'lose', weight: 50, color: '#1A1A1D', labelColor: '#F2F9FF' },
  ];

  const totalWeight = segments.reduce((sum, s) => sum + s.weight, 0);
  let acc = 0;
  const slices = segments.map((s) => {
    const angle = (s.weight / totalWeight) * 360;
    const start = acc; const end = acc + angle; const center = start + angle / 2; acc = end;
    return { ...s, start, end, center, angle };
  });

  useEffect(() => {
    if (!state.loading && !state.user) setShowSignIn(true);
  }, [state.loading, state.user]);

  function handlePrizeSelection(prize: Prize) {
    setSelectedPrize(prize);
    setShowPrizeSelection(false);
  }

  function spin() {
    if (!state.user) { setShowSignIn(true); return; }
    if (spinning) return;
    // Enforce 7-day cooldown
    const now = Date.now();
    if (nextAllowedAt && now < nextAllowedAt) {
      setResult(t('wheelV2.cooldownNext', { time: formatRemaining(nextAllowedAt - now) }));
      return;
    }
    setResult(null);
    const winIndex = segments.findIndex((s) => s.id === 'win');
    const index = winIndex >= 0 ? winIndex : 0;
    setSelectedIndex(index);
    const selected = slices[index];
    const baseOrientation = 360 - selected.center;
    setSpinning(false);
    setWheelRotation(baseOrientation);
    persistLastSpin(now).then(() => setNextAllowedAt(now + COOLDOWN_MS));
    requestAnimationFrame(() => { requestAnimationFrame(() => {
      setSpinning(true);
      const fullSpins = 5 + Math.floor(Math.random() * 3);
      const half = selected.angle / 2;
      const margin = Math.min(10, Math.max(4, selected.angle * 0.08));
      const allowed = Math.max(0, half - margin);
      const fractions = [0.95, 0.9, 0.85, 0.75, 0.6];
      const baseCandidates = fractions.flatMap((f) => [ Math.round(allowed * f), -Math.round(allowed * f) ]);
      const candidates = baseCandidates.map((v) => Math.max(-allowed, Math.min(allowed, v)));
      const jitter = candidates[Math.floor(Math.random() * candidates.length)] || 0;
      const target = baseOrientation + jitter + fullSpins * 360;
      setWheelRotation(target);
    }); });
  }

  function handleSpinEnd() {
    if (selectedIndex != null) {
      const seg = segments[selectedIndex];
      const won = seg.id === 'win';
      const displayLabel = won ? t('wheelV2.segments.win') : t('wheelV2.segments.lose');
      setResult(displayLabel);

      if (won && selectedPrize) {
        const id = generateCouponId();
        setCouponId(id);
        setShowCouponModal(true);
        const email = state.user?.email;
        if (email) {
          (async () => {
            try {
              const response = await fetch('/.netlify/functions/send-coupon-v2', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  email,
                  code: id,
                  prize: selectedPrize.title,
                  prizeDescription: selectedPrize.description,
                  locale: i18n.language
                }),
              });
              if (!response.ok) {
                const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
                console.error('[coupon] Email send failed:', response.status, errorData);
              }
            } catch (err) {
              console.error('[coupon] Email send error:', err);
            }
          })();
        }
      }
    }
    setSpinning(false);
  }

  function generateCouponId(): string {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let rand = '';
    for (let i = 0; i < 8; i++) rand += alphabet[Math.floor(Math.random() * alphabet.length)];
    const y = new Date().getFullYear().toString().slice(-2);
    const m = String(new Date().getMonth() + 1).padStart(2, '0');
    const d = String(new Date().getDate()).padStart(2, '0');
    return `210-${y}${m}${d}-${rand}`;
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!showCouponModal || !couponId || !barcodeRef.current) return;
      try {
        const { default: JsBarcode } = await import('jsbarcode');
        if (cancelled) return;
        JsBarcode(barcodeRef.current, couponId, { format: 'CODE128', width: 2, height: 64, displayValue: false, margin: 0, background: '#ffffff', lineColor: '#111827' });
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [showCouponModal, couponId]);

  async function persistLastSpin(ts: number) {
    try { await supabase.auth.updateUser({ data: { wheelLastSpinAt: ts } }); } catch {}
    try { if (state.user) localStorage.setItem(`wheelLastSpinAt:${state.user.id}`, String(ts)); } catch {}
  }

  function formatRemaining(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    if (days > 0) return `${days}d ${hours}h`;
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  }

  function formatCountdownPrecise(ms: number): string {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    if (days > 0) return `${days}d ${pad(hours)}h ${pad(minutes)}m`;
    if (hours > 0) return `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
    return `${pad(minutes)}m ${pad(seconds)}s`;
  }

  return (
    <div className="text-white min-h-screen relative flex flex-col">
          <Helmet>
  <title>Spin & Win | 210 Tattoo Athens</title>
  <meta name="robots" content="noindex, nofollow" />
  <meta name="description" content="Γύρνα τη ρόδα και κέρδισε έκπτωση στο επόμενο τατουάζ σου στο 210 Tattoo Athens." />
  <link rel="canonical" href="https://210tattoo.gr/spin" />
</Helmet>
      <div className="fixed inset-0 -z-10 bg-gradient-to-br from-gray-950 via-black to-gray-900" aria-hidden="true" />
      <div className="fixed inset-0 -z-10" aria-hidden="true" style={{ backgroundImage: `url(${spinBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div className="fixed inset-0 -z-10 bg-black/60" aria-hidden="true" />
      <nav className="fixed top-0 w-full bg-black/95 backdrop-blur-sm z-50 border-b border-pink-600/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            <div className="flex-shrink-0 flex items-center">
              <a href="/" className="flex items-center gap-3">
                <picture>
                  <source srcSet={`${logoImage80} 1x, ${logoImage160} 2x, ${logoImage240} 3x`} type="image/jpeg" />
                  <img src={logoImage80} srcSet={`${logoImage80} 1x, ${logoImage160} 2x, ${logoImage240} 3x`} alt="210 Tattoo Studio" className="h-20 w-auto rounded-lg object-contain" width={80} height={80} loading="eager" decoding="async" />
                </picture>
              </a>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-center space-x-8">
                <a href="/#home" className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400">{t('nav.home')}</a>
                <div className="relative group">
                  <button className="px-3 py-2 text-sm font-medium text-gray-300 group-hover:text-pink-400">{t('nav.studioMenu')}</button>
                  <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 mt-2 w-48 rounded-md bg-black/95 border border-pink-600/20 shadow-lg py-2 z-50">
                    <a href="/#about" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.about')}</a>
                    <a href="/#services" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.services')}</a>
                  </div>
                </div>
                <a href="/#gallery" className="px-3 py-2 text-sm font-medium text-gray-300 hover:text-pink-400">{t('nav.gallery')}</a>
                <div className="relative group">
                  <button className="px-3 py-2 text-sm font-medium text-gray-300 group-hover:text-pink-400">{t('nav.helpMenu')}</button>
                  <div className="invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-opacity absolute left-0 mt-2 w-56 rounded-md bg-black/95 border border-pink-600/20 shadow-lg py-2 z-50">
                    <a href="/#faq" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.faq')}</a>
                    <a href="/#contact" className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:text-white hover:bg-white/5">{t('nav.contact')}</a>
                  </div>
                </div>
                {spinWheelEnabled !== false && (
                  <a href="/spin" className="px-3 py-2 text-sm font-semibold text-white bg-gradient-to-r from-pink-600 to-pink-700 rounded-lg hover:from-pink-700 hover:to-pink-800 shadow-lg hover:shadow-pink-500/25">{t('nav.spin')}</a>
                )}
                <div className="flex items-center gap-2 pl-4 border-l border-pink-600/20">
                  <button onClick={() => loadLanguage('el')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'el' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`} aria-label="Switch to Greek">ΕΛ</button>
                  <button onClick={() => loadLanguage('en')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'en' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`} aria-label="Switch to English">EN</button>
                </div>
                <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-lg hover:shadow-pink-500/25">
                  <Instagram size={16} />
                  {t('nav.dmCta')}
                </a>
                <AuthMenu />
              </div>
            </div>
            <div className="md:hidden flex items-center gap-2">
              <AuthMenu />
              <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="text-gray-300 hover:text-white p-2" aria-label={isMenuOpen ? t('nav.closeMenu') : t('nav.openMenu')} aria-expanded={isMenuOpen} aria-controls="mobile-menu">
                {isMenuOpen ? <X size={24} aria-hidden /> : <Menu size={24} aria-hidden />}
              </button>
            </div>
          </div>
        </div>
        {isMenuOpen && (
          <div id="mobile-menu" className="md:hidden bg-black border-t border-pink-600/20">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {(['home','about','services','gallery','faq','contact'] as const).map((id) => (
                <a key={id} href={`/#${id}`} className="block px-3 py-2 text-base font-medium w-full text-left text-gray-300 hover:text-pink-400">{t(`nav.${id}` as any)}</a>
              ))}
              {spinWheelEnabled !== false && (
                <a href="/spin" className="block px-3 py-2 text-base font-medium w-full text-left text-pink-300 hover:text-pink-400">{t('nav.spin')}</a>
              )}
              <div className="flex items-center gap-2 mt-2 px-3">
                <button onClick={() => loadLanguage('el')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'el' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`}>ΕΛ</button>
                <button onClick={() => loadLanguage('en')} className={`px-2 py-1 text-xs rounded ${i18n.language === 'en' ? 'bg-pink-600 text-white' : 'text-gray-300 hover:text-white'}`}>EN</button>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-4 px-3 pb-3 items-center justify-items-center mx-3">
                <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="bg-gradient-to-r from-pink-600 to-pink-700 hover:from-pink-700 hover:to-pink-800 px-3 py-2 rounded-lg text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 justify-self-center">
                  <Instagram size={16} />
                  {t('nav.dmCta')}
                </a>
                <div className="flex justify-center justify-self-center"><AuthMenu /></div>
              </div>
            </div>
          </div>
        )}
      </nav>

      <main className="pt-28 flex-1">
        {spinWheelEnabled === false ? (
          <section className="relative min-h-[60vh] flex items-center justify-center px-4">
            <div className="w-full max-w-lg mx-auto text-center">
              <div className="rounded-xl overflow-hidden border border-gray-800 bg-black/80 backdrop-blur-sm p-10">
                <h1 className="text-3xl font-bold mb-4">{t('nav.spin')}</h1>
                <p className="text-gray-300 mb-6">
                  {i18n.language === 'el'
                    ? 'Ο τροχός δεν είναι διαθέσιμος αυτή τη στιγμή. Δοκίμασε ξανά αργότερα!'
                    : 'The spin wheel is currently unavailable. Please check back later!'}
                </p>
                <a
                  href="/"
                  className="inline-block px-6 py-3 rounded-lg bg-pink-600 hover:bg-pink-700 text-white font-semibold transition-colors"
                >
                  {i18n.language === 'el' ? 'Επιστροφή στην αρχική' : 'Back to Home'}
                </a>
              </div>
            </div>
          </section>
        ) : (
        <section className="relative min-h-[60vh] flex items-center justify-center px-4">
          {showPrizeSelection ? (
            <div className="relative w-full max-w-4xl mx-auto">
              <div className="rounded-xl overflow-hidden border border-gray-800 bg-black/80 backdrop-blur-sm p-8">
                <h1 className="text-3xl font-bold mb-4 text-center">{t('wheelV2.selectPrize')}</h1>
                <p className="text-gray-300 mb-8 text-center">{t('wheelV2.selectPrizeSubtitle')}</p>

                <div className="grid md:grid-cols-2 gap-4">
                  {prizes.map((prize) => (
                    <button
                      key={prize.id}
                      onClick={() => handlePrizeSelection(prize)}
                      disabled={!state.user}
                      className="p-6 rounded-lg border-2 border-pink-600/30 hover:border-pink-600 bg-black/40 hover:bg-black/60 transition-all duration-200 text-left disabled:opacity-50 disabled:cursor-not-allowed group"
                    >
                      <h3 className="text-xl font-bold text-pink-400 mb-2 group-hover:text-pink-300">{prize.title}</h3>
                      <p className="text-gray-300 text-sm">{prize.description}</p>
                    </button>
                  ))}
                </div>

                {!state.user && (
                  <div className="mt-6 text-center text-sm text-gray-300">{t('wheelV2.signInPrompt')}</div>
                )}
              </div>
            </div>
          ) : (
            <div className="relative w-full max-w-5xl mx-auto grid md:grid-cols-2 gap-6">
              <div className="rounded-xl overflow-hidden border border-gray-800 text-center bg-black/80 backdrop-blur-sm">
                <div className="relative z-10 p-6">
                  <h1 className="text-2xl font-bold mb-2">{t('wheel.title', 'Spin the Wheel')}</h1>
                  <div className="mb-4 p-3 rounded-lg bg-pink-600/20 border border-pink-600/40">
                    <p className="text-sm text-gray-200">{t('wheelV2.playingFor')}</p>
                    <p className="text-lg font-bold text-pink-300">{selectedPrize?.title}</p>
                  </div>
                <div className="relative mx-auto mb-6 w-64 h-64 select-none">
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2 z-20" aria-hidden="true">
                    <div className="w-2 h-2 border-l-8 border-r-8 border-b-12 border-l-transparent border-r-transparent border-b-pink-500 drop-shadow" />
                  </div>
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 z-20" aria-hidden="true">
                    <div className="w-2 h-2 rounded-full bg-white border-2 border-pink-500" />
                  </div>
                  <div className="absolute inset-0 rounded-full border-4 border-pink-600 shadow-2xl" aria-hidden="true" />
                  <svg className="relative w-full h-full" viewBox="-50 -50 100 100" style={{ transform: `rotate(${wheelRotation}deg)`, transition: spinning ? 'transform 5.5s cubic-bezier(.12,.11,0,1)' : 'none' }} onTransitionEnd={handleSpinEnd}>
                    {slices.map((s, i) => {
                      const r = 48;
                      const toXY = (a: number) => { const rad = (a - 90) * Math.PI / 180; return { x: r * Math.cos(rad), y: r * Math.sin(rad) }; };
                      const p1 = toXY(s.start); const p2 = toXY(s.end); const largeArc = s.angle > 180 ? 1 : 0;
                      const mid = s.center; const labelRadius = 30; const lx = labelRadius * Math.cos((mid - 90) * Math.PI / 180); const ly = labelRadius * Math.sin((mid - 90) * Math.PI / 180);
                      let rot = mid - 90; if (mid > 180) rot += 180;
                      const displayLabel = s.id === 'win' ? t('wheelV2.segments.win') : t('wheelV2.segments.lose');
                      const fontSize = 7;
                      return (
                        <g key={i}>
                          <path d={`M0 0 L${p1.x} ${p1.y} A${r} ${r} 0 ${largeArc} 1 ${p2.x} ${p2.y} Z`} fill={(s as any).color} />
                          <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" transform={`rotate(${rot} ${lx} ${ly})`} style={{ fontSize, fontWeight: 700, fill: (s as any).labelColor || '#ffffff' }}>
                            {displayLabel}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center"><div className="w-10 h-10 rounded-full bg-black border-2 border-pink-500 shadow-inner" /></div>
                </div>
                  {(() => {
                    const now = Date.now();
                    const available = !nextAllowedAt || now >= nextAllowedAt;
                    const label = spinning
                      ? t('wheelV2.spinning')
                      : (available ? t('wheelV2.spin') : t('wheelV2.cooldownNext', { time: countdownText || formatRemaining((nextAllowedAt || now) - now) }));
                    return (
                      <>
                        <button onClick={spin} disabled={spinning || !available} className="px-6 py-3 rounded-lg bg-pink-600 hover:bg-pink-700 disabled:opacity-50 font-semibold mb-3">{label}</button>
                        <button onClick={() => { setShowPrizeSelection(true); setResult(null); }} className="block mx-auto text-sm text-gray-400 hover:text-pink-400 underline">
                          {t('wheelV2.changePrize')}
                        </button>
                      </>
                    );
                  })()}
                  {result && <div className="mt-6 text-lg text-pink-300 font-semibold">{result}</div>}
              </div>
            </div>
              {/* Prize Details / How to Claim Card */}
              <div className="rounded-xl overflow-hidden border border-gray-800 bg-black/80 backdrop-blur-sm p-6">
                <h2 className="text-xl font-bold mb-2">{t('wheelV2.howItWorks')}</h2>
                <p className="text-gray-100 text-sm mb-6">{t('wheelV2.howItWorksSubtitle')}</p>

                <div>
                  <h3 className="text-lg font-semibold text-gray-100 mb-3">{t('wheelV2.availablePrizes')}</h3>
                  <ul className="space-y-3 text-sm text-gray-100">
                    {prizes.map((prize) => (
                      <li key={prize.id} className="p-3 rounded-lg bg-black/40 border border-pink-600/20">
                        <div className="font-semibold text-pink-300">{prize.title}</div>
                        <div className="text-xs text-gray-300">{prize.description}</div>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-8 space-y-2 text-sm text-gray-100">
                  <div className="font-semibold text-gray-100">{t('wheelV2.howToClaimTitle')}</div>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>{t('wheelV2.steps.s1')}</li>
                    <li>{t('wheelV2.steps.s2')}</li>
                    <li>{t('wheelV2.steps.s3')}</li>
                    <li>{t('wheelV2.steps.s4')}</li>
                  </ol>
                </div>

                <div className="mt-8 p-4 rounded-lg border border-pink-600/30 bg-pink-600/10 space-y-2 text-sm">
                  <div className="font-semibold text-pink-300">{t('wheelV2.terms.title')}</div>
                  <ul className="space-y-2 text-gray-200">
                    <li className="flex items-start gap-2">
                      <span className="text-pink-400 mt-0.5">•</span>
                      <span>{t('wheelV2.terms.validity')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-pink-400 mt-0.5">•</span>
                      <span>{t('wheelV2.terms.comboRestriction')}</span>
                    </li>
                  </ul>
                </div>

                {result && selectedIndex === 0 && (
                  <div className="mt-4 rounded-lg border border-emerald-600/30 bg-emerald-600/10 p-3 text-emerald-200 text-sm">
                    {t('wheelV2.wonMessage', { prize: selectedPrize?.title || '' })}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
        )}
      </main>

      {showCouponModal && couponId && selectedPrize && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md bg-white text-gray-900 rounded-xl shadow-2xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-pink-600 text-white">
              <h3 className="text-lg font-bold">{t('wheelV2.coupon.title')}</h3>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="p-3 rounded-lg bg-pink-50 border border-pink-200">
                <p className="text-sm font-semibold text-gray-700">{t('wheelV2.coupon.yourPrize')}</p>
                <p className="text-lg font-bold text-pink-600">{selectedPrize.title}</p>
                <p className="text-xs text-gray-600">{selectedPrize.description}</p>
              </div>
              <p className="text-sm">{t('wheelV2.coupon.subtitle')}</p>
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 flex flex-col items-center gap-3 overflow-hidden">
                <div className="text-xs uppercase tracking-wide text-gray-500">{t('wheelV2.coupon.code')}</div>
                <div className="text-2xl font-extrabold text-gray-900">{couponId}</div>
                <div className="mt-2 w-full max-w-full">
                  <svg ref={barcodeRef} style={{ width: '100%', height: 64 }} aria-label={t('wheel.barcodeAlt')} role="img" />
                </div>
              </div>
              <div className="text-sm text-gray-700">
                {t('wheelV2.coupon.emailNotice')}
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-300">
                <p className="text-xs font-semibold text-amber-900 mb-2">{t('wheelV2.terms.title')}</p>
                <ul className="space-y-1 text-xs text-amber-800">
                  <li className="flex items-start gap-1">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span>{t('wheelV2.terms.validity')}</span>
                  </li>
                  <li className="flex items-start gap-1">
                    <span className="text-amber-600 mt-0.5">•</span>
                    <span>{t('wheelV2.terms.comboRestriction')}</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
              <button onClick={() => setShowCouponModal(false)} className="px-4 py-2 rounded-lg bg-pink-600 text-white hover:bg-pink-700 font-semibold">{t('wheelV2.coupon.close')}</button>
            </div>
          </div>
        </div>
      )}

      <SignInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} onOpenRegister={() => { /* optional registration */ }} />

      <footer className="bg-black py-12 border-t border-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h3 className="text-3xl font-bold text-pink-500 mb-4">{t('footer.studio')}</h3>
            <p className="text-gray-400 mb-6">{t('footer.tagline')}</p>
            <div className="flex justify-center space-x-6 mb-8">
              <a href="https://www.instagram.com/210tattoo/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-pink-500 transition-colors duration-200" aria-label={t('footer.instagramLabel', 'Visit our Instagram profile')}>
                <Instagram size={24} aria-hidden />
              </a>
            </div>
            <nav className="mb-8">
              <ul className="flex justify-center gap-6 text-sm">
                <li><Link to="/privacy" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">{t('footer.privacy', 'Privacy Policy')}</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">{t('footer.cookies', 'Cookies Policy')}</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-pink-400 underline-offset-4 hover:underline">{t('footer.terms', 'Terms of Service')}</Link></li>
              </ul>
            </nav>
            <div className="border-t border-gray-800 pt-8"><p className="text-gray-300 text-sm">{t('footer.rights')}</p></div>
          </div>
        </div>
      </footer>
    </div>
  );
}


